import { useEffect, useState, useMemo } from 'react';
import { ShoppingBag, Plus, Minus, Trash2, X, MessageCircle, UtensilsCrossed } from 'lucide-react';
import { listTable } from '@/lib/api';
import type { Category, Product, Settings, CartItem } from '@/types';
import { formatPrice, formatWhatsAppNumber, buildWhatsAppMessage, buildWhatsAppUrl, normalizePublicImageUrl } from '@/lib/format';

export function CustomerView() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [catRes, prodRes, setRes] = await Promise.all([
      listTable<Category>('categories'),
      listTable<Product>('products'),
      listTable<Settings>('settings').then((rows) => rows[0] ?? null),
    ]);
    setCategories(catRes || []);
    setProducts(prodRes || []);
    setSettings(setRes);
    setLoading(false);
  };

  const filteredProducts = useMemo(() => {
    if (activeCategory === 'all') return products;
    return products.filter((p) => p.category_id === activeCategory);
  }, [products, activeCategory]);

  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);
  const cartTotal = cart.reduce((sum, i) => sum + i.product.price * i.quantity, 0);

  const addToCart = (product: Product) => {
    if (!product.is_active) return;
    setCart((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const updateQty = (productId: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((i) =>
          i.product.id === productId
            ? { ...i, quantity: Math.max(0, i.quantity + delta) }
            : i
        )
        .filter((i) => i.quantity > 0)
    );
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((i) => i.product.id !== productId));
  };

  const sendOrder = () => {
    if (!settings || cart.length === 0) return;
    const number = formatWhatsAppNumber(settings.whatsapp_number);
    if (!number) {
      alert('O WhatsApp ainda não foi configurado pelo estabelecimento.');
      return;
    }
    const message = buildWhatsAppMessage(settings.business_name, cart);
    window.open(buildWhatsAppUrl(number, message), '_blank');
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-warm-200 border-t-brand-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-28">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-warm-200/60 bg-white/90 backdrop-blur-lg">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
          {settings?.logo_url ? (
            <img
              src={normalizePublicImageUrl(settings.logo_url)}
              alt="Logo"
              className="h-11 w-11 rounded-xl object-cover ring-1 ring-warm-200"
            />
          ) : (
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500 text-white">
              <UtensilsCrossed size={22} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="truncate font-display text-lg font-bold text-warm-900">
              {settings?.business_name || 'Cardápio'}
            </h1>
            <p className="text-xs text-warm-500">Cardápio Online</p>
          </div>
          <button
            onClick={() => setCartOpen(true)}
            className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500 text-white shadow-sm transition-all hover:bg-brand-600 active:scale-95"
            aria-label="Abrir carrinho"
          >
            <ShoppingBag size={20} />
            {cartCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-warm-900 px-1 text-xs font-bold text-white animate-scale-in">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Category tabs */}
      <nav className="sticky top-[65px] z-20 border-b border-warm-200/60 bg-warm-50/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl gap-2 overflow-x-auto px-4 py-3 [scrollbar-width:none]">
          <CategoryTab
            label="Todos"
            active={activeCategory === 'all'}
            onClick={() => setActiveCategory('all')}
          />
          {categories.map((cat) => (
            <CategoryTab
              key={cat.id}
              label={cat.name}
              active={activeCategory === cat.id}
              onClick={() => setActiveCategory(cat.id)}
            />
          ))}
        </div>
      </nav>

      {/* Products grid */}
      <main className="mx-auto max-w-5xl px-4 py-5">
        {filteredProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <UtensilsCrossed size={48} className="text-warm-300" />
            <p className="mt-4 text-warm-500">Nenhum produto cadastrado ainda.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAdd={() => addToCart(product)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Floating cart bar */}
      {cartCount > 0 && !cartOpen && (
        <button
          onClick={() => setCartOpen(true)}
          className="fixed bottom-5 left-1/2 z-30 flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-center justify-between rounded-2xl bg-warm-900 px-5 py-3.5 text-white shadow-xl animate-slide-up"
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            <ShoppingBag size={18} />
            {cartCount} {cartCount === 1 ? 'item' : 'itens'}
          </span>
          <span className="flex items-center gap-2">
            <span className="font-bold">{formatPrice(cartTotal)}</span>
            <span className="text-warm-400">→</span>
          </span>
        </button>
      )}

      {/* Cart drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-warm-900/40 backdrop-blur-sm animate-fade-in"
            onClick={() => setCartOpen(false)}
          />
          <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl animate-slide-in-right">
            <div className="flex items-center justify-between border-b border-warm-100 px-5 py-4">
              <h2 className="font-display text-lg font-semibold text-warm-900">Seu Pedido</h2>
              <button
                onClick={() => setCartOpen(false)}
                className="rounded-full p-2 text-warm-500 transition-colors hover:bg-warm-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <ShoppingBag size={48} className="text-warm-300" />
                  <p className="mt-4 text-warm-500">Seu carrinho está vazio</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cart.map((item) => (
                    <div
                      key={item.product.id}
                      className="flex gap-3 rounded-2xl bg-warm-50 p-3"
                    >
                      {item.product.image_url && (
                        <img
                          src={normalizePublicImageUrl(item.product.image_url)}
                          alt={item.product.name}
                          className="h-16 w-16 flex-shrink-0 rounded-xl object-cover"
                        />
                      )}
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-medium text-warm-900">{item.product.name}</h3>
                          <button
                            onClick={() => removeFromCart(item.product.id)}
                            className="text-warm-400 transition-colors hover:text-red-500"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <span className="text-sm text-warm-500">
                          {formatPrice(item.product.price)}
                        </span>
                        <div className="mt-auto flex items-center justify-between pt-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQty(item.product.id, -1)}
                              className="flex h-7 w-7 items-center justify-center rounded-lg bg-white text-warm-700 ring-1 ring-warm-200 transition-colors hover:bg-warm-100"
                            >
                              <Minus size={14} />
                            </button>
                            <span className="w-6 text-center text-sm font-semibold">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQty(item.product.id, 1)}
                              className="flex h-7 w-7 items-center justify-center rounded-lg bg-white text-warm-700 ring-1 ring-warm-200 transition-colors hover:bg-warm-100"
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                          <span className="font-semibold text-warm-900">
                            {formatPrice(item.product.price * item.quantity)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-warm-100 px-5 py-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-warm-600">Total</span>
                  <span className="font-display text-xl font-bold text-warm-900">
                    {formatPrice(cartTotal)}
                  </span>
                </div>
                <button
                  onClick={sendOrder}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-5 py-3.5 font-semibold text-white shadow-sm transition-all hover:bg-[#1eb555] active:scale-[0.98]"
                >
                  <MessageCircle size={20} />
                  Enviar Pedido no WhatsApp
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-all ${
        active
          ? 'bg-brand-500 text-white shadow-sm'
          : 'bg-white text-warm-600 ring-1 ring-warm-200 hover:bg-warm-100'
      }`}
    >
      {label}
    </button>
  );
}

function ProductCard({ product, onAdd }: { product: Product; onAdd: () => void }) {
  const inactive = !product.is_active;

  return (
    <div
      className={`group card overflow-hidden transition-all ${
        inactive ? 'opacity-60' : 'hover:shadow-md hover:-translate-y-0.5'
      }`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-warm-100">
        {product.image_url ? (
          <img
            src={normalizePublicImageUrl(product.image_url)}
            alt={product.name}
            className={`h-full w-full object-cover transition-transform duration-500 ${
              inactive ? 'grayscale' : 'group-hover:scale-105'
            }`}
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-warm-100 text-warm-300">
            <UtensilsCrossed size={40} />
          </div>
        )}
        {inactive && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70">
            <span className="rounded-lg bg-warm-900/80 px-3 py-1.5 text-sm font-semibold text-white">
              Produto Indisponível
            </span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-display font-semibold text-warm-900">{product.name}</h3>
        {product.description && (
          <p className="mt-1 line-clamp-2 text-sm text-warm-500">{product.description}</p>
        )}
        <div className="mt-3 flex items-center justify-between">
          <span className="font-display text-lg font-bold text-brand-600">
            {formatPrice(product.price)}
          </span>
          <button
            onClick={onAdd}
            disabled={inactive}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-500 text-white shadow-sm transition-all hover:bg-brand-600 active:scale-95 disabled:cursor-not-allowed disabled:bg-warm-300"
            aria-label="Adicionar ao carrinho"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
